import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {Observable} from 'rxjs';
import {flatMap} from 'rxjs/internal/operators';
import {Store} from '@ngrx/store';

import { FuseConfirmDialogComponent } from '@fuse/components/confirm-dialog/confirm-dialog.component';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { AclService } from '@aes/acl/acl.service';
import {selectEmployeeState} from '@aes/core/core.state';

import {ApprovalRequestStatus, EntityId, ErrorDialogComponent, IApprovalRequest} from '@appshared';
import { ApprovalRequestApiService } from './approval-request-api.service';

@Injectable({
  providedIn: 'root',
})
export class MyRequestService {
  private _confirmDialogRef: MatDialogRef<any>;

  constructor(
    private _matDialog: MatDialog,
    private _translate: TranslateService,
    private _fuseProgressBarService: FuseProgressBarService,
    private _approvalRequestApiSrv: ApprovalRequestApiService,
    private _store: Store<any>,
    private _aclService: AclService
  ) { }

  submit(data: any): void {
    this._confirmDialogRef = this._matDialog.open(FuseConfirmDialogComponent, {
      disableClose: true,
    });
    this._confirmDialogRef.componentInstance.data = {
      confirmHeader: this._translate.instant('DIALOG.SUBMIT_REQUEST.HEADER'),
      confirmMessage: this._translate.instant('DIALOG.SUBMIT_REQUEST.MESSAGE'),
      acceptBtnText: this._translate.instant('DIALOG.SUBMIT_REQUEST.ACCEPT_BUTTON_TEXT'),
      cancelBtnText: this._translate.instant('DIALOG.SUBMIT_REQUEST.CANCEL_BUTTON_TEXT'),
    };

    this._confirmDialogRef.afterClosed().subscribe(result => {
      this._fuseProgressBarService.show();
      if (result && data) {
        // TODO: integrate  - Submmit a request
      }
      this._fuseProgressBarService.hide();
      this._confirmDialogRef = null;
    });
    return;
  }

  cancel(reqIds: EntityId[], name?: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const totalOfRequestIds = reqIds.length;
      this._confirmDialogRef = this._matDialog.open(FuseConfirmDialogComponent, {
        disableClose: true,
      });
      this._confirmDialogRef.componentInstance.data = {
        svgIcon: 'artstuff_cancel',
        confirmHeader: this._translate.instant('DIALOG.CANCEL_MY_REQUEST.HEADER'),
        confirmMessage: this._translate.instant('DIALOG.CANCEL_MY_REQUEST.MESSAGE', {
          name: name,
        }),
        acceptBtnText: this._translate.instant('DIALOG.CANCEL_MY_REQUEST.ACCEPT_BUTTON_TEXT'),
        cancelBtnText: this._translate.instant('DIALOG.CANCEL_MY_REQUEST.CANCEL_BUTTON_TEXT'),
      };
      if (totalOfRequestIds > 2) {
        this._confirmDialogRef.componentInstance.data.confirmMessage = this._translate.instant('DIALOG.CANCEL_MY_REQUEST.MESSAGE', {
          count: totalOfRequestIds,
        });
      }

      this._confirmDialogRef.afterClosed().subscribe(result => {
        if (result && reqIds) {
          this._fuseProgressBarService.show();
          this._confirmDialogRef = null;
          this._approvalRequestApiSrv.updateStatus(reqIds, ApprovalRequestStatus.CANCELED).subscribe(
            res => {
              this._fuseProgressBarService.hide();
              resolve(true);
            },
            err => {
              this._fuseProgressBarService.hide();
              this._matDialog.open(ErrorDialogComponent, {
                disableClose: true,
              });
              reject();
            }
          );
        }
      });
    });
  }

  /**
   * Check if the current logged in user have permission to cancel their own approved request
   */
  canCancelApprovedRequest(approvedRequest: IApprovalRequest): Observable<boolean> {
    return this._store.select(selectEmployeeState).pipe(flatMap((employee) => {
      const isCancelOwnApprovedRequest =
          approvedRequest.requester.id === employee.userId &&
          approvedRequest.status === ApprovalRequestStatus.APPROVED;
      if (isCancelOwnApprovedRequest) {
        return new Observable<boolean>(subscriber => {
          subscriber.next(this._aclService.hasAnyPermission(['PERM_CANCEL_MY_APPROVED_REQUEST']));
        });
      }
      return new Observable<boolean>(subscriber => {
        subscriber.next(this._aclService.hasAnyPermission(['PERM_CANCEL_APPROVAL']));
      });
    }));
  }

  /**
   * additional text in request type column
   */
  buildAdditionalRequestTypeInfo(approvalRequest: IApprovalRequest): string {
    let additionString = '';
    if (approvalRequest.requestType === 'OVERTIME') {
      const totalWorkingTime = approvalRequest.endDate - approvalRequest.startDate; // seconds
      const hour = Math.round(totalWorkingTime / 3600);
      let hourDisplay = 'HOURS';
      if (hour < 1) {
        hourDisplay = 'HOUR';
      }
      additionString = hour + ' ' + this._translate.instant('APPROVAL_REQUESTS.' + hourDisplay).toString();
    }
    return additionString;
  }
}
